import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, Mail, Key, ShieldCheck, Loader2, Eye, EyeOff, School, Users } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Reset Password
  const [identifier, setIdentifier] = useState('');
  const [schoolCode, setSchoolCode] = useState('');
  const [userType, setUserType] = useState('student');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailMissing, setEmailMissing] = useState(false);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(apiUrl('users/password-reset/request/'), {
        identifier,
        school_code: schoolCode,
        user_type: userType
      });
      setSessionId(res.data.session_id);
      // Check if email is missing
      if (res.data.email_missing) {
        setEmailMissing(true);
        setSuccess('Please add your email address below to complete password reset.');
      } else {
        setSuccess('OTP has been sent to your registered email.');
      }
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters long');
    if (emailMissing && (!email || !email.includes('@'))) return setError('Email is required. Please enter a valid email address.');
    
    // Normalize OTP code - strip whitespace and ensure it's exactly 6 digits
    const normalizedOTP = otpCode.trim().replace(/\D/g, '');
    if (normalizedOTP.length !== 6) {
      return setError('OTP must be exactly 6 digits');
    }

    setLoading(true);
    try {
      const res = await axios.post(apiUrl('users/password-reset/confirm/'), {
        session_id: sessionId,
        otp_code: normalizedOTP,
        new_password: newPassword,
        confirm_password: confirmPassword,
        email: emailMissing ? email : undefined  // Only send email if it's missing
      });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => window.location.href = '/login', 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please check your OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen font-sans bg-[hsl(var(--main-bg))]">
      <div className="w-full md:w-1/2 flex flex-col justify-center px-10 md:px-20 bg-[hsl(var(--main-bg))] relative overflow-y-auto">
        <div className="mb-8 mt-10 flex flex-col items-center text-center">
          <div className="mb-6">
            <img 
              src="/logo_square.png" 
              alt="Budlee AI" 
              className="h-32 w-32 sm:h-40 sm:w-40 object-contain animate-logo-jump" 
            />
          </div>

          <h2 className="text-xl font-semibold text-foreground mb-2">
            {step === 1 ? 'Forgot Password?' : 'Reset Your Password'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {step === 1
              ? "We'll send a 6-digit code to your registered email."
              : "Enter the code we sent and choose a new password."}
          </p>
        </div>

        <div className="space-y-6 mb-10">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3">
              <p className="text-sm text-green-500 font-medium">{success}</p>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">User Type</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <select
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      className="block w-full pl-10 pr-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground appearance-none"
                      required
                      disabled={loading}
                    >
                      <option value="student">Student</option>
                      <option value="teacher">Teacher</option>
                      <option value="admin">School Admin</option>
                      <option value="guardian">Guardian</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">School Code</label>
                  <div className="relative">
                    <School className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={schoolCode}
                      onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                      className="block w-full pl-10 pr-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                      placeholder="e.g. SCH001"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="block w-full pl-10 pr-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                    placeholder="Enter your username"
                    required
                    disabled={loading}
                    autoComplete="username"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white py-2 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Request OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">6-Digit OTP</label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    className="block w-full pl-10 pr-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground tracking-[0.5em] font-mono"
                    placeholder="000000"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {emailMissing && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-4 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                      placeholder="Enter your email address"
                      required
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-yellow-400 mt-1">⚠️ Email is required for account recovery</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">New Password</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                    placeholder="Enter new password (min 8 characters)"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Confirm New Password</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white py-2 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset Password'}
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-sm text-muted-foreground hover:text-foreground underline"
              >
                Change Username
              </button>
            </form>
          )}

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 w-full border border-[hsl(var(--card-border))] text-foreground py-2 rounded-lg hover:bg-white/5 transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
        </div>
      </div>

      <div className="hidden md:block w-1/2 relative bg-gradient-to-br from-[hsl(var(--green-bright))]/10 to-[hsl(var(--teal-vivid))]/10">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="min-h-[96px] flex items-center justify-center mb-6">
              <img
                src="/logo_square.png"
                alt="Budlee AI Logo"
                className="h-32 sm:h-40 mx-auto object-contain"
                style={{ maxWidth: '300px' }}
              />
            </div>
            <p className="text-muted-foreground font-medium">Verify your identity with an OTP sent to your email.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

